import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Button, Text } from '#atoms';
import getFirstDayOfWeek from '#utils/getFirstDayOfWeek';
import getWeekNumber from '#utils/getWeekNumber';
import { CalendarPeriodic } from 'src/features/Calendar/types';
import { MONTHS, WEEK } from './constants';
import ScrollContainer from './ScrollContainer';
import * as S from './styles';
import { getDayOptions, getYearsRange } from './utils';

type DayDate = Date | string | dayjs.Dayjs;

interface Props {
  calendarPeriodic?: CalendarPeriodic;
  date?: DayDate;
  onClose: () => void;
  onSubmit?: (date: dayjs.Dayjs) => void;
}

interface DateObj {
  year: number;
  month: number;
  week: number;
  day: number;
}

export default function DaySelectModal({
  date,
  calendarPeriodic,
  onClose,
  onSubmit
}: Props) {
  const [activeDate, setActiveDate] = useState<dayjs.Dayjs>(
    date ? dayjs(date) : dayjs()
  );
  const [dateObj, setDateObj] = useState<DateObj>({} as DateObj);

  const handleChange = (obj: Partial<DateObj>) => {
    const newState = { ...dateObj, ...obj };

    if (newState.year && newState.month) {
      if (calendarPeriodic === 'WEEKLY') {
        const currentDate = dayjs(`${newState.year}-${newState.month}`);
        const firstDay = getFirstDayOfWeek(
          dayjs(currentDate).add(newState.week - 1, 'week')
        );

        if (dayjs(firstDay).get('month') !== currentDate.get('month')) {
          setActiveDate(currentDate);
        } else {
          setActiveDate(firstDay);
        }
      } else {
        const currentDate = dayjs(
          `${newState.year}-${newState.month}-${newState.day}`
        );
        setActiveDate(currentDate);
      }

      setDateObj(newState);
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(activeDate);
    }

    onClose();
  };

  useEffect(() => {
    const _date = dayjs(date);
    const week = getWeekNumber(_date);

    setDateObj({
      year: _date.get('year'),
      month: _date.get('month') + 1,
      week,
      day: _date.get('date')
    });
  }, [date]);

  return (
    <S.Modal onClose={onClose}>
      <S.Container>
        <S.ModalTitle>
          <Text textStyle="bb">날짜 선택</Text>
        </S.ModalTitle>
        <div>
          <S.ScrollWrapper>
            <ScrollContainer
              width={86}
              defaultValue={String(dateObj.year)}
              options={getYearsRange()}
              surfix="년"
              onChange={value => {
                handleChange({ year: Number(value) });
              }}
            />
            <S.Divider />
            <ScrollContainer
              width={66}
              defaultValue={String(dateObj.month)}
              options={MONTHS}
              surfix="월"
              onChange={value => {
                handleChange({ month: Number(value) });
              }}
            />
            <S.Divider />
            {calendarPeriodic === 'WEEKLY' ? (
              <ScrollContainer
                defaultValue={String(dateObj.week)}
                options={WEEK}
                surfix="째주"
                onChange={value => {
                  handleChange({ week: Number(value) });
                }}
              />
            ) : (
              <ScrollContainer
                defaultValue={String(dateObj.day)}
                options={getDayOptions(dateObj.year, dateObj.month)}
                surfix="일"
                onChange={value => {
                  handleChange({ day: Number(value) });
                }}
              />
            )}
          </S.ScrollWrapper>
        </div>
      </S.Container>
      <S.ButtonBox>
        <Button background="anti" onClick={onClose}>
          취소
        </Button>
        <Button onClick={handleSubmit}>확인</Button>
      </S.ButtonBox>
    </S.Modal>
  );
}
